/**
 * Provenance display rules for federated (unified) search hits (M6 / M6A).
 *
 * A hit's chips communicate where it came from:
 *  - Corpus hits → a single "Corpus" chip (optionally the ingest agent, e.g. "OSINT").
 *  - Data-item hits the viewer already owns → "My Data".
 *  - Data-item hits owned by another user → "Uploaded by …".
 *  - Report hits → a "Products" chip with the report classification (FINTEL / OSINT / …) as a
 *    secondary qualifier (falls back to the report title when no classification is set).
 *
 * Pure, side-effect free — unit tested in __tests__/sourcesProvenance.test.js.
 */

export const PROVENANCE_KIND = Object.freeze({
    corpus: 'corpus',
    myFile: 'my_file',
    uploadedBy: 'uploaded_by',
    sharedWithMe: 'shared_with_me',
    collectedMaterial: 'collected_material',
    report: 'report',
    highlight: 'highlight',
});

/** Truncate long report titles used as the secondary chip qualifier. */
function truncate(text, max = 48) {
    const value = String(text || '').trim();
    if (value.length <= max) {
        return value;
    }
    return `${value.slice(0, max - 1)}…`;
}

/**
 * Derive the provenance chips for a unified search hit.
 *
 * @param {object} hit unified search hit (`documentStore`, `ownership`, …)
 * @returns {{kind: string, primary: string, secondary: (string|null)}}
 *   `primary` is the main chip label; `secondary` is an optional qualifier (agent name / uploader / report title).
 */
export function getProvenanceDisplay(hit) {
    const store = hit?.documentStore;
    const ownership = hit?.ownership || {};

    if (store === 'corpus') {
        const agent = ownership.corpusAgentDisplayName || ownership.corpusAgent || null;
        return {
            kind: PROVENANCE_KIND.corpus,
            primary: ownership.label || 'Corpus',
            secondary: agent ? String(agent).toUpperCase() : null,
        };
    }

    if (store === 'report') {
        const classification = ownership.reportClassification || null;
        return {
            kind: PROVENANCE_KIND.report,
            primary: 'Products',
            secondary: classification
                ? String(classification).toUpperCase()
                : (ownership.reportTitle ? truncate(ownership.reportTitle) : null),
        };
    }

    if (store === 'highlight') {
        return {
            kind: PROVENANCE_KIND.highlight,
            primary: 'Highlight',
            secondary: null,
        };
    }

    // data_item store
    if (ownership.collectedMaterial) {
        const cm = ownership.cmNumber || null;
        const ct = ownership.ctNumber || null;
        const secondary = [ct, cm].filter(Boolean).join(' · ') || null;
        return {
            kind: PROVENANCE_KIND.collectedMaterial,
            primary: 'Collected Materials',
            secondary,
        };
    }

    if (ownership.inMyFiles) {
        return {
            kind: PROVENANCE_KIND.myFile,
            primary: 'My Files',
            secondary: null,
        };
    }

    if (ownership.sharedWithMe) {
        const sharer = ownership.sharedByDisplayName || ownership.sharedByUserId || null;
        return {
            kind: PROVENANCE_KIND.sharedWithMe,
            primary: sharer ? `Shared by ${sharer}` : 'Shared with me',
            secondary: null,
        };
    }

    const uploader = ownership.uploadedByDisplayName || ownership.uploadedBy || null;
    return {
        kind: PROVENANCE_KIND.uploadedBy,
        primary: uploader ? `Uploaded by ${uploader}` : 'Uploaded',
        secondary: null,
    };
}

/**
 * Derive provenance (kind + label) from just the raw {@code docSourceType} sentinel, for callers
 * that don't have a full unified-search hit/ownership object to feed {@link getProvenanceDisplay} —
 * e.g. Source Package tray items (only `docSourceType` survives the drag-and-drop save) and
 * margin-rail citations. Same {kind, primary, secondary} shape so both feed the same chip
 * rendering, keeping every "source" badge visually identical regardless of how much provenance
 * detail is available.
 *
 * Recognizes the same legacy sentinel casings the margin-rail citation list has always accepted
 * (`MY FILE`, `DATA_ITEM_REFERENCE`, `SOURCE-FILE`, …) so existing saved citations don't relabel.
 *
 * @param {string} docSourceType raw stored sentinel (`MY_DATA`, `REPORT`, `HIGHLIGHT`, `CORPUS`, …)
 * @returns {{kind: string, primary: string, secondary: null}}
 */
export function getProvenanceFromDocSourceType(docSourceType) {
    switch (String(docSourceType || '').toUpperCase()) {
        case 'MY_DATA':
        case 'FILE':
        case 'MY FILE':
        case 'DATA_ITEM_REFERENCE':
        case 'SOURCE-FILE':
            return {kind: PROVENANCE_KIND.myFile, primary: 'My Files', secondary: null};
        case 'REPORT':
        case 'REPORTS':
        case 'REPORT_SEGMENT':
            return {kind: PROVENANCE_KIND.report, primary: 'Products', secondary: null};
        case 'HIGHLIGHT':
        case 'HIGHLIGHTS':
            return {kind: PROVENANCE_KIND.highlight, primary: 'Highlight', secondary: null};
        default:
            return {kind: PROVENANCE_KIND.corpus, primary: 'Corpus', secondary: null};
    }
}

/** MUI chip color per provenance kind. */
export function getProvenanceChipColor(kind) {
    switch (kind) {
        case PROVENANCE_KIND.corpus:
            return 'primary';
        case PROVENANCE_KIND.myFile:
            return 'success';
        case PROVENANCE_KIND.collectedMaterial:
            return 'success';
        case PROVENANCE_KIND.report:
            return 'warning';
        case PROVENANCE_KIND.highlight:
            return 'secondary';
        case PROVENANCE_KIND.sharedWithMe:
            return 'info';
        case PROVENANCE_KIND.uploadedBy:
            return 'default';
        default:
            return 'default';
    }
}
